import { useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { FileCheck, Loader2, Plus, Search, SearchX, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { HeroCard } from "@/components/waggle/HeroCard";
import { PageHeader, PageShell } from "@/components/waggle/PageShell";
import { TestList } from "@/components/waggle/TestList";
import { useDevMode } from "@/context/DevModeContext";
import { useCreateTest } from "@/hooks/use-create-test";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { buildSampleTestPayload } from "@/utils/sampleTest";
import { type Test } from "@shared/schema";

type SortKey = "recent" | "oldest" | "name-asc" | "name-desc";

const SORT_LABEL: Record<SortKey, string> = {
  recent: "Most recent",
  oldest: "Oldest first",
  "name-asc": "Name A → Z",
  "name-desc": "Name Z → A",
};

/** My Tests: every saved test, with search and sort. */
export default function MyTests() {
  const { toast } = useToast();
  const { devMode } = useDevMode();
  const { createTest, atTestLimit, showLimitToast, confirmDialog } = useCreateTest();

  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<SortKey>("recent");

  const { data: tests, isLoading } = useQuery<Test[]>({
    queryKey: ["/api/tests"],
  });

  // Filters by name substring (case-insensitive) and sorts in the order the
  // user picked. Returns a stable derived list so React Query results stay
  // untouched.
  const filteredSortedTests = useMemo(() => {
    if (!tests) return [];
    const needle = search.trim().toLowerCase();
    const filtered = needle
      ? tests.filter((t) => t.name.toLowerCase().includes(needle))
      : [...tests];
    const byDateAsc = (a: Test, b: Test) =>
      new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    const byName = (a: Test, b: Test) => a.name.localeCompare(b.name);
    switch (sort) {
      case "recent":
        filtered.sort((a, b) => byDateAsc(b, a));
        break;
      case "oldest":
        filtered.sort(byDateAsc);
        break;
      case "name-asc":
        filtered.sort(byName);
        break;
      case "name-desc":
        filtered.sort((a, b) => byName(b, a));
        break;
    }
    return filtered;
  }, [tests, search, sort]);

  // Dev convenience — POSTs the hand-crafted sample payload as if it
  // had just been finalized. Server attaches createdBy from the auth
  // middleware, so the row only shows up for the logged-in user.
  const seedSampleMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/tests", buildSampleTestPayload());
      return res.json() as Promise<Test>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/tests"] });
      toast({ title: "Sample test added", description: "Open it from the list below." });
    },
    onError: () => {
      toast({ title: "Could not add sample", description: "Try again.", variant: "destructive" });
    },
  });

  const handleSeedSample = () => {
    if (atTestLimit) {
      showLimitToast();
      return;
    }
    seedSampleMutation.mutate();
  };

  const hasTests = !!tests && tests.length > 0;

  return (
    <PageShell>
      <PageHeader
        title="My Tests"
        description="Every test you've finished — open, download, or reuse one."
        action={
          /* Dev convenience: add a fully-fleshed-out sample test so you can
             play with the open / preview / edit / download flows without
             running the workflow. Gated behind dev mode (the secret top-left
             Dev options panel). */
          devMode && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleSeedSample}
              disabled={seedSampleMutation.isPending}
              data-testid="button-add-sample-test"
            >
              {seedSampleMutation.isPending ? "Adding…" : "Add sample test"}
            </Button>
          )
        }
      />

      {hasTests && (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name"
              className="w-full pl-9 sm:w-64"
              data-testid="input-search-tests"
            />
          </div>
          <Select value={sort} onValueChange={(v) => setSort(v as SortKey)}>
            <SelectTrigger className="w-full sm:w-48" data-testid="select-sort-tests">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              {(Object.keys(SORT_LABEL) as SortKey[]).map((key) => (
                <SelectItem key={key} value={key}>
                  {SORT_LABEL[key]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : !hasTests ? (
        <HeroCard
          icon={<FileCheck />}
          title="No tests yet"
          description="Tests you finish will show up here, ready to open, download, or reuse. Start with a worksheet or slide deck you've already taught from."
          actions={
            <Button onClick={createTest}>
              Create a test <Plus />
            </Button>
          }
        />
      ) : filteredSortedTests.length === 0 ? (
        <HeroCard
          tone="muted"
          icon={<SearchX />}
          title={<>No tests match &ldquo;{search}&rdquo;</>}
          description="Try a different name, or clear the search to see all your tests."
          actions={
            <Button variant="outline" onClick={() => setSearch("")}>
              Clear search <X />
            </Button>
          }
        />
      ) : (
        <TestList tests={filteredSortedTests} />
      )}
      {confirmDialog}
    </PageShell>
  );
}
